import React, { useEffect, useRef, useState } from "react";
import { uploadVideo } from "../../services/apiContent";

const MATURITY_OPTIONS = ["7+", "10+", "13+", "16+", "18+"];

function FilmUploadPanel({ users, categories }) {
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [userId, setUserId] = useState("");
  const [categorieId, setCategorieId] = useState("");
  const [maturityRating, setMaturityRating] = useState("13+");
  const [isDragActive, setIsDragActive] = useState(false);
  const [image1, setImage1] = useState(null);
  const [image2, setImage2] = useState(null);
  const [previewImage1, setPreviewImage1] = useState(null);
  const [previewImage2, setPreviewImage2] = useState(null);

  const inputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleImageSelect = (event, setImage, setPreview) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setImage(selectedFile);
    setPreview(URL.createObjectURL(selectedFile));
  };

  const handleFile = (selectedFile) => {
    if (!selectedFile) return;
    if (!selectedFile.type.startsWith("video/")) {
      alert("Veuillez deposer un fichier video.");
      return;
    }

    if (previewUrl) {
      try {
        URL.revokeObjectURL(previewUrl);
      } catch {
        // ignore
      }
    }

    setFile(selectedFile);
    setPreviewUrl(URL.createObjectURL(selectedFile));
  };

  const handleUpload = async () => {
    if (!file) {
      alert("Veuillez selectionner une video.");
      return;
    }

    setIsUploading(true);
    setMessage("");
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("video", file);
    formData.append("title", title);
    formData.append("description", description);
    formData.append("type", "film");
    formData.append("categorie_id", categorieId);
    formData.append("user_id", userId);
    formData.append("maturity_rating", maturityRating);
    if (image1) formData.append("video_img_1", image1);
    if (image2) formData.append("video_img_2", image2);

    try {
      await uploadVideo(formData, (progressEvent) => {
        if (progressEvent?.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });

      setMessage("Film ajoute avec succes.");
      setFile(null);
      setTitle("");
      setDescription("");
      setPreviewUrl(null);
      setUploadProgress(0);
      setImage1(null);
      setImage2(null);
      setPreviewImage1(null);
      setPreviewImage2(null);
      setUserId("");
      setCategorieId("");
      setMaturityRating("13+");

      if (inputRef.current) {
        inputRef.current.value = "";
      }
    } catch (error) {
      console.error("Erreur upload film:", error);
      setMessage("Erreur lors de l'upload du film.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="upload-page">
      <div className="upload-card">
        <h2 className="upload-subtitle">Ajouter un film</h2>
        <p className="upload-helper">
          Flux unitaire pour les films avec video principale, affiches, classification parentale et metadonnees detectees automatiquement.
        </p>

        <div
          className={`upload-dropzone ${isDragActive ? "upload-dropzone--active" : ""}`}
          onDragEnter={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setIsDragActive(true);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            event.stopPropagation();
            event.dataTransfer.dropEffect = "copy";
            setIsDragActive(true);
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setIsDragActive(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setIsDragActive(false);
            handleFile(event.dataTransfer?.files?.[0]);
          }}
        >
          {previewUrl ? (
            <video src={previewUrl} controls className="upload-preview" />
          ) : (
            <div className="upload-placeholder">
              <p>Glissez la video du film ici ou cliquez pour choisir un fichier.</p>
            </div>
          )}

          <input
            ref={inputRef}
            type="file"
            accept="video/*"
            onChange={(event) => handleFile(event.target.files?.[0])}
            className="upload-input"
          />

          <div
            className="upload-click-here"
            onClick={() => inputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key === "Enter") inputRef.current?.click();
            }}
            tabIndex={0}
            role="button"
          >
            <button type="button" className="upload-choose-btn">
              Choisir un fichier
            </button>
          </div>
        </div>

        {isUploading ? (
          <div className="upload-progress">
            <div className="upload-progress-bar" style={{ width: `${uploadProgress}%` }} />
            <p>{uploadProgress}%</p>
          </div>
        ) : null}

        <div className="upload-images">
          <div className="upload-image-field">
            <p className="upload-label">Image principale</p>
            <div className="upload-box">
              {previewImage1 ? (
                <img src={previewImage1} alt="Affiche principale" className="upload-thumb" />
              ) : (
                <div className="upload-placeholder">
                  <span>Affiche principale</span>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(event) => handleImageSelect(event, setImage1, setPreviewImage1)}
                className="upload-input"
              />
            </div>
          </div>

          <div className="upload-image-field">
            <p className="upload-label">Image secondaire</p>
            <div className="upload-box">
              {previewImage2 ? (
                <img src={previewImage2} alt="Affiche secondaire" className="upload-thumb" />
              ) : (
                <div className="upload-placeholder">
                  <span>Backdrop / second visuel</span>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(event) => handleImageSelect(event, setImage2, setPreviewImage2)}
                className="upload-input"
              />
            </div>
          </div>
        </div>

        <div className="upload-form">
          <input type="text" placeholder="Titre du film" value={title} onChange={(event) => setTitle(event.target.value)} />
          <textarea
            placeholder="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={4}
          />

          <div className="upload-grid">
            <select value={categorieId} onChange={(event) => setCategorieId(event.target.value)} disabled={!categories.length}>
              <option value="">{categories.length ? "Selectionner une categorie" : "Aucune categorie"}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.title}
                </option>
              ))}
            </select>

            <select value={userId} onChange={(event) => setUserId(event.target.value)} disabled={!users.length}>
              <option value="">{users.length ? "Selectionner un acteur / proprietaire" : "Aucun acteur"}</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.nom} - {user.phone}
                </option>
              ))}
            </select>

            <select value={maturityRating} onChange={(event) => setMaturityRating(event.target.value)}>
              {MATURITY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  Classification {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="upload-actions">
          <button onClick={handleUpload} disabled={isUploading}>
            {isUploading ? "Chargement..." : "Publier le film"}
          </button>
        </div>

        {message ? <p className="upload-message">{message}</p> : null}
      </div>
    </div>
  );
}

export default FilmUploadPanel;
